import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import {
  IonTabs,
  IonRouterOutlet,
  IonTabBar,
  IonTabButton,
  IonIcon,
  IonLabel,
  IonBadge,
} from "@ionic/react";
import {
  pulse,
  shuffleOutline,
  people,
  trophy,
  chatbubbleOutline,
} from "ionicons/icons";
import { useQuery } from "@apollo/client/react";
import { ActivityTab } from "./activity/ActivityTab";
import { MessagesTab } from "./messages/MessagesTab";
import { MarketTab } from "./market/MarketTab";
import { TeamTab } from "./team/TeamTab";
import { PointsTab } from "./points/PointsTab";
import { PastLeagueMemberLineupPage } from "./points/PastLeagueMemberLineupPage";
import { FantasyLeagueMemberTeamPage } from "./points/FantasyLeagueMemberTeamPage";
import { AddPlayerToMarket } from "./market/AddPlayerToMarket";
import { ReceivedBidsPage } from "./market/ReceivedBidsPage";
import { PastBidsPage } from "./market/PastBidsPage";
import { GET_FANTASY_LEAGUE_TABS_SHELL } from "../../graphql/queries/fantasyLeagueTabsShell";
import type {
  GetFantasyLeagueTabsShellQuery,
  GetFantasyLeagueTabsShellQueryVariables,
} from "../../types/graphql-generated";
import { useEffect, useRef } from 'react';
import { ReferenceCompetitionProvider } from '../../contexts/ReferenceCompetitionContext';

// Ionic's outlet also falls back to the index route for league paths none of
// these tabs own (e.g. the standalone `/app/ligas/:leagueId/ajustes` page), so
// only redirect when the URL is the league root itself.
const LeagueRootRedirect = ({ leaguePath }: { leaguePath: string }) => {
  const { pathname } = useLocation();
  const isLeagueRoot = pathname.replace(/\/$/, '') === leaguePath;

  return isLeagueRoot ? <Navigate to="equipo" replace /> : null;
};

export const FantasyLeagueTabsPage = () => {
  const leagueId = useParams<'leagueId'>().leagueId!;
  const { pathname } = useLocation();
  const previousPathnameRef = useRef(pathname);

  const { data, refetch } = useQuery<
    GetFantasyLeagueTabsShellQuery,
    GetFantasyLeagueTabsShellQueryVariables
  >(GET_FANTASY_LEAGUE_TABS_SHELL, {
    variables: { fantasyLeagueId: leagueId },
    fetchPolicy: 'cache-and-network',
  });

  const currentMember = data?.currentFantasyLeagueMember;
  // Players in the league's screens are flagged as "No está en la liga" when
  // they no longer play in the league's competition.
  const leagueCompetitionId = data?.fantasyLeague?.competition.id;
  const leaguePath = `/app/ligas/${leagueId}`;

  // Revalidate if Ionic restores this cached tab page after visiting a route
  // outside the league. Internal league tab changes share the same shell data.
  useEffect(() => {
    const previousPathname = previousPathnameRef.current;
    previousPathnameRef.current = pathname;
    const isLeaguePath = pathname === leaguePath || pathname.startsWith(`${leaguePath}/`);
    const wasLeaguePath =
      previousPathname === leaguePath || previousPathname.startsWith(`${leaguePath}/`);

    if (isLeaguePath && !wasLeaguePath) {
      void refetch({ fantasyLeagueId: leagueId }).catch(() => undefined);
    }
  }, [leagueId, leaguePath, pathname, refetch]);

  return (
    <ReferenceCompetitionProvider value={leagueCompetitionId}>
      <IonTabs>
        <IonRouterOutlet>
          <Routes>
            <Route
              path="actividad"
              element={
                <ActivityTab
                  leagueId={leagueId}
                  memberId={currentMember?.id}
                  activityLastReadAt={currentMember?.activityLastReadAt}
                />
              }
            />
            <Route
              path="mensajes"
              element={
                <MessagesTab
                  leagueId={leagueId}
                  memberId={currentMember?.id}
                  messagesLastReadAt={currentMember?.messagesLastReadAt}
                />
              }
            />
            <Route
              path="mensajes/plantilla/:fantasyLeagueMemberId"
              element={
                <FantasyLeagueMemberTeamPage
                  leagueId={leagueId}
                  defaultBackHref={`${leaguePath}/mensajes`}
                />
              }
            />
            <Route path="mercado" element={<MarketTab leagueId={leagueId} />} />
            <Route path="mercado/recibidas" element={<ReceivedBidsPage leagueId={leagueId} />} />
            <Route path="mercado/pasadas" element={<PastBidsPage leagueId={leagueId} />} />
            <Route path="mercado/add-player" element={<AddPlayerToMarket />} />
            <Route
              path="mercado/plantilla/:fantasyLeagueMemberId"
              element={
                <FantasyLeagueMemberTeamPage
                  leagueId={leagueId}
                  defaultBackHref={`${leaguePath}/mercado`}
                />
              }
            />
            <Route path="equipo" element={<TeamTab leagueId={leagueId} />} />
            <Route path="puntos/:matchWeekId?" element={<PointsTab leagueId={leagueId} />} />
            <Route
              path="puntos/plantilla/:fantasyLeagueMemberId"
              element={
                <FantasyLeagueMemberTeamPage
                  leagueId={leagueId}
                  defaultBackHref={`${leaguePath}/puntos`}
                />
              }
            />
            <Route
              path="puntos/jornada/:matchWeekId/:fantasyLeagueMemberId"
              element={<PastLeagueMemberLineupPage leagueId={leagueId} />}
            />
            <Route
              index
              element={<LeagueRootRedirect leaguePath={leaguePath} />}
            />
          </Routes>
        </IonRouterOutlet>

        <IonTabBar slot="bottom" id="app-footer">
          <IonTabButton tab="equipo" href={`/app/ligas/${leagueId}/equipo`}>
            <IonIcon icon={people} />
            <IonLabel>Equipo</IonLabel>
          </IonTabButton>
          <IonTabButton tab="actividad" href={`/app/ligas/${leagueId}/actividad`}>
            <IonIcon icon={pulse} />
            <IonLabel>Actividad</IonLabel>
            {currentMember?.unreadEventsCount ? (
              <IonBadge color="danger">
                {currentMember.unreadEventsCount}
              </IonBadge>
            ) : null}
          </IonTabButton>
          <IonTabButton tab="mensajes" href={`/app/ligas/${leagueId}/mensajes`}>
            <IonIcon icon={chatbubbleOutline} />
            <IonLabel>Mensajes</IonLabel>
            {currentMember?.unreadMessagesCount ? (
              <IonBadge color="danger">
                {currentMember.unreadMessagesCount}
              </IonBadge>
            ) : null}
          </IonTabButton>
          <IonTabButton tab="puntos" href={`/app/ligas/${leagueId}/puntos`}>
            <IonIcon icon={trophy} />
            <IonLabel>Puntos</IonLabel>
          </IonTabButton>
          <IonTabButton tab="mercado" href={`/app/ligas/${leagueId}/mercado`}>
            <IonIcon icon={shuffleOutline} />
            <IonLabel>Fichajes</IonLabel>
          </IonTabButton>
        </IonTabBar>
      </IonTabs>
    </ReferenceCompetitionProvider>
  );
};
